import React, { useState, useEffect, useRef, useCallback } from "react";
import { BatchEventEntity, BatchDocumentReference, BatchLifecycleEvent } from "../types";
import { getStatusInfo, formatDateTime, toPascalCase } from "../batchStatusHelper";
import { getBatchDocuments, getBatchEvents, downloadBatchDocuments, downloadBatchPayload, downloadBatchB2CReport } from "../batchDashboardService";

interface BatchDetailDrawerProps {
  batch: BatchEventEntity | null;
  useUtc: boolean;
  onClose: () => void;
  getAccessToken: () => Promise<string>;
}

type TabKey = "timeline" | "documents" | "errors";

const categoryColors: Record<string, string> = {
  BatchCreated: "#0078D4",
  BatchDocumentStaged: "#107C10",
  BatchReadyToSend: "#CA5010",
  BatchSent: "#0078D4",
  BatchCallbackReceived: "#8764B8",
  BatchCompleted: "#107C10",
  BatchFailed: "#D13438",
};

// Turn a service-layer error (which carries the underlying HTTP status code from
// batchDashboardService.ts, e.g. "DownloadBatchB2CReport failed: 404") into a short, user-facing
// message. We surface the status code so a 404 from a not-yet-deployed endpoint is distinguishable
// from a 401/403 (auth/role) or 5xx (server) — otherwise the previous silent console.error left
// users with no signal at all.
function formatDownloadError(label: string, err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  const statusMatch = message.match(/\b(\d{3})\b/);
  const status = statusMatch ? Number(statusMatch[1]) : undefined;
  if (status === 401 || status === 403) {
    return `${label} failed: you don't have permission for this batch (HTTP ${status}).`;
  }
  if (status === 404) {
    return `${label} failed: endpoint not found (HTTP 404). The backend may not yet be deployed to this environment.`;
  }
  if (status && status >= 500) {
    return `${label} failed: server error (HTTP ${status}). Please retry; if it persists, check service health.`;
  }
  if (status) {
    return `${label} failed (HTTP ${status}).`;
  }
  return `${label} failed: ${message}`;
}

export const BatchDetailDrawer: React.FC<BatchDetailDrawerProps> = ({ batch, useUtc, onClose, getAccessToken }) => {
  const [activeTab, setActiveTab] = useState<TabKey>("timeline");
  const [documents, setDocuments] = useState<BatchDocumentReference[]>([]);
  const [docTotalCount, setDocTotalCount] = useState(0);
  const [docSkip, setDocSkip] = useState(0);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [timelineEvents, setTimelineEvents] = useState<BatchLifecycleEvent[]>([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (batch) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
    }
  }, [batch]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }

    if (e.key === "Tab" && dialogRef.current) {
      const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    }
  }, [onClose]);

  // Lock body scroll when drawer is open to prevent background scrolling
  useEffect(() => {
    if (batch) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [batch]);

  useEffect(() => {
    if (batch && activeTab === "timeline") {
      loadTimeline();
    }
  }, [batch, activeTab]);

  useEffect(() => {
    if (batch && activeTab === "documents") {
      loadDocuments(0);
    }
  }, [batch, activeTab]);

  const loadTimeline = async () => {
    if (!batch) return;
    setLoadingTimeline(true);
    try {
      const token = await getAccessToken();
      const result = await getBatchEvents(batch.batchId, batch.partitionKey, token);
      setTimelineEvents(result.batchEvents || []);
    } catch (err) {
      console.error("Failed to load timeline", err);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const loadDocuments = async (skip: number) => {
    if (!batch) return;
    setLoadingDocs(true);
    try {
      const token = await getAccessToken();
      const result = await getBatchDocuments(batch.batchId, batch.partitionKey, token, skip, 25);
      setDocuments(result.documents);
      setDocTotalCount(result.totalCount);
      setDocSkip(skip);
    } catch (err) {
      console.error("Failed to load documents", err);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleDownload = async () => {
    if (!batch) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const token = await getAccessToken();
      await downloadBatchDocuments(batch.batchId, batch.partitionKey, token, batch.countryCode, batch.companyCode, batch.configType);
    } catch (err) {
      console.error("Failed to download batch", err);
      setDownloadError(formatDownloadError("Download Documents", err));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPayload = async () => {
    if (!batch) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const token = await getAccessToken();
      await downloadBatchPayload(batch.batchId, batch.partitionKey, token, batch.countryCode, batch.companyCode, batch.configType);
    } catch (err) {
      console.error("Failed to download batch payload", err);
      setDownloadError(formatDownloadError("Download Batch", err));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadB2CReport = async () => {
    if (!batch) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const token = await getAccessToken();
      await downloadBatchB2CReport(batch.batchId, batch.partitionKey, token, batch.countryCode, batch.companyCode, batch.configType);
    } catch (err) {
      console.error("Failed to download B2C report", err);
      setDownloadError(formatDownloadError("B2C Report (CSV)", err));
    } finally {
      setDownloading(false);
    }
  };

  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  if (!batch) return null;

  const isBuffering = batch.status.toLowerCase() === "staging" &&
    (batch.bufferExpiryDateTime != null || timelineEvents.some(e => e.message?.includes("Buffering")));
  const statusInfo = getStatusInfo(isBuffering ? "buffering" : batch.status);
  const hasErrors = batch.errors && batch.errors.length > 0;

  const availableTabs: TabKey[] = ["timeline", "documents", ...(hasErrors ? ["errors" as TabKey] : [])];

  const handleTabKeyDown = (e: React.KeyboardEvent, tab: TabKey) => {
    const currentIndex = availableTabs.indexOf(tab);
    let newIndex = -1;

    if (e.key === "ArrowRight") {
      e.preventDefault();
      newIndex = (currentIndex + 1) % availableTabs.length;
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      newIndex = (currentIndex - 1 + availableTabs.length) % availableTabs.length;
    } else if (e.key === "Home") {
      e.preventDefault();
      newIndex = 0;
    } else if (e.key === "End") {
      e.preventDefault();
      newIndex = availableTabs.length - 1;
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setActiveTab(tab);
      return;
    }

    if (newIndex >= 0) {
      const tabButton = dialogRef.current?.querySelector<HTMLElement>(`[data-tab="${availableTabs[newIndex]}"]`);
      tabButton?.focus();
    }
  };

  const tabStyle = (tab: TabKey): React.CSSProperties => ({
    padding: "0.5rem 1rem",
    cursor: "pointer",
    borderBottom: activeTab === tab ? "0.125rem solid #0078D4" : "0.125rem solid transparent",
    color: activeTab === tab ? "#0078D4" : "#605E5C",
    fontWeight: activeTab === tab ? 600 : 400,
    fontSize: '0.8125rem',
    backgroundColor: "transparent",
    border: "none",
  });

  return (
    <>
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 999 }} onClick={onClose} aria-hidden="true" tabIndex={-1} />
      <div ref={dialogRef} role="dialog" aria-label="Batch Detail" aria-modal="true" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: '40rem', maxWidth: '100vw', backgroundColor: "#FFFFFF", boxShadow: "-0.25rem 0 1.5rem rgba(0,0,0,0.15)", zIndex: 1000, display: "flex", flexDirection: "column", overflow: "hidden" }} onKeyDown={handleKeyDown}>
        {/* Header */}
        <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid #EDEBE9" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: '0.5rem' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: "#323130", margin: 0 }}>Batch Detail</h2>
              <div style={{ fontSize: '0.75rem', fontFamily: "monospace", color: "#605E5C", marginTop: '0.25rem', wordBreak: "break-all" }}>{batch.batchId}</div>
            </div>
        <button ref={closeButtonRef} onClick={onClose} aria-label="Close batch detail" title="Close the batch detail panel and return to the batch list" style={{ background: "none", border: "1px solid transparent", fontSize: '1.25rem', cursor: "pointer", color: "#605E5C", padding: "0.25rem 0.5rem", borderRadius: '0.25rem', position: "relative", zIndex: 1, minWidth: '2rem', minHeight: '2rem', flexShrink: 0 }}><span aria-hidden="true">✕</span></button>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: '0.25rem', padding: "0.25rem 0.75rem", borderRadius: '1rem', fontSize: '0.8125rem', fontWeight: 600, color: statusInfo.color, backgroundColor: statusInfo.bgColor }}>
              <span aria-hidden="true">{statusInfo.icon}</span> {statusInfo.label}
            </span>
          </div>
          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "auto 1fr auto 1fr", gap: "8px 8px", fontSize: 13, alignItems: "baseline" }}>
            <span style={{ color: "#605E5C" }}>Country:</span><strong>{batch.countryCode}</strong>
            <span style={{ color: "#605E5C" }}>Company Code:</span><strong>{batch.companyCode}</strong>
            <span style={{ color: "#605E5C" }}>Batch Type:</span><strong>{toPascalCase(batch.configType)}</strong>
            <span style={{ color: "#605E5C" }}>Documents:</span><strong>{batch.documentCount.toLocaleString()}</strong>
            {batch.reportPeriodStart && (
              <>
                <span style={{ color: "#605E5C" }}>Report Period:</span>
                <strong style={{ gridColumn: "2 / -1" }}>{new Date(batch.reportPeriodStart).toLocaleDateString()} — {batch.reportPeriodEnd ? new Date(batch.reportPeriodEnd).toLocaleDateString() : ""}</strong>
              </>
            )}
          </div>
          <div style={{ marginTop: '1rem', position: 'relative', display: 'inline-block' }}>
            <button onClick={() => setShowDownloadMenu(!showDownloadMenu)} disabled={downloading} aria-haspopup="true" aria-expanded={showDownloadMenu} aria-label="Download options" title="Download batch data" style={{ padding: "0.5rem 1.25rem", backgroundColor: "#0078D4", color: "#FFFFFF", border: "none", borderRadius: '0.25rem', fontSize: '0.8125rem', fontWeight: 600, cursor: downloading ? "not-allowed" : "pointer", opacity: downloading ? 0.6 : 1, minHeight: '2rem' }}>
              {downloading ? <><span aria-hidden="true">⏳ </span>Downloading...</> : <><span aria-hidden="true">📥 </span>Download ▾</>}
            </button>
            {showDownloadMenu && !downloading && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', background: '#fff', border: '1px solid #E1DFDD', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 10, minWidth: '200px' }} role="menu">
                <button onClick={() => { setShowDownloadMenu(false); handleDownload(); }} role="menuitem" style={{ display: 'block', width: '100%', padding: '8px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem' }} onMouseOver={(e) => (e.currentTarget.style.background = '#F3F2F1')} onFocus={(e) => (e.currentTarget.style.background = '#F3F2F1')} onMouseOut={(e) => (e.currentTarget.style.background = 'none')} onBlur={(e) => (e.currentTarget.style.background = 'none')}>
                  📄 Download Documents
                </button>
                <button onClick={() => { setShowDownloadMenu(false); handleDownloadPayload(); }} role="menuitem" style={{ display: 'block', width: '100%', padding: '8px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem' }} onMouseOver={(e) => (e.currentTarget.style.background = '#F3F2F1')} onFocus={(e) => (e.currentTarget.style.background = '#F3F2F1')} onMouseOut={(e) => (e.currentTarget.style.background = 'none')} onBlur={(e) => (e.currentTarget.style.background = 'none')}>
                  📦 Download Batch
                </button>
                {batch.countryCode?.toUpperCase() === "FR" && (
                  <button onClick={() => { setShowDownloadMenu(false); handleDownloadB2CReport(); }} role="menuitem" title="Download a CSV of B2C documents (no customer Tax ID) in this batch" style={{ display: 'block', width: '100%', padding: '8px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem' }} onMouseOver={(e) => (e.currentTarget.style.background = '#F3F2F1')} onFocus={(e) => (e.currentTarget.style.background = '#F3F2F1')} onMouseOut={(e) => (e.currentTarget.style.background = 'none')} onBlur={(e) => (e.currentTarget.style.background = 'none')}>
                    📊 B2C Report (CSV)
                  </button>
                )}
              </div>
            )}
            {downloadError && (
              <div role="alert" aria-live="polite" style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', backgroundColor: '#FDE7E9', border: '1px solid #D13438', borderRadius: '0.25rem', color: '#A4262C', fontSize: '0.8125rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', maxWidth: '32rem' }}>
                <span aria-hidden="true" style={{ flexShrink: 0 }}>⚠</span>
                <span style={{ flex: 1, wordBreak: 'break-word' }}>{downloadError}</span>
                <button onClick={() => setDownloadError(null)} aria-label="Dismiss download error" title="Dismiss" style={{ background: 'none', border: 'none', color: '#A4262C', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: 0, flexShrink: 0 }}>✕</button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #EDEBE9", padding: "0 1.5rem", flexWrap: "wrap" }} role="tablist" aria-label="Batch detail tabs">
          <button style={tabStyle("timeline")} onClick={() => setActiveTab("timeline")} onKeyDown={(e) => handleTabKeyDown(e, "timeline")} role="tab" aria-selected={activeTab === "timeline"} aria-controls="tabpanel-timeline" id="tab-timeline" tabIndex={activeTab === "timeline" ? 0 : -1} data-tab="timeline">Timeline</button>
          <button style={tabStyle("documents")} onClick={() => setActiveTab("documents")} onKeyDown={(e) => handleTabKeyDown(e, "documents")} role="tab" aria-selected={activeTab === "documents"} aria-controls="tabpanel-documents" id="tab-documents" tabIndex={activeTab === "documents" ? 0 : -1} data-tab="documents">Documents</button>
          {hasErrors && <button style={tabStyle("errors")} onClick={() => setActiveTab("errors")} onKeyDown={(e) => handleTabKeyDown(e, "errors")} role="tab" aria-selected={activeTab === "errors"} aria-controls="tabpanel-errors" id="tab-errors" tabIndex={activeTab === "errors" ? 0 : -1} data-tab="errors"><span aria-hidden="true">⚠ </span>Errors ({batch.errors.length})</button>}
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, overflow: "auto", padding: 24 }} tabIndex={0} role="region" aria-label="Batch detail content">
          {activeTab === "timeline" && (
            <div>
              {loadingTimeline ? (
                <div style={{ textAlign: "center", padding: 40, color: "#605E5C" }}>Loading timeline...</div>
              ) : timelineEvents.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#A19F9D" }}>No timeline events</div>
              ) : (
                timelineEvents.map((event, index) => (
                  <div key={index} style={{ display: "flex", gap: 16 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 20 }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: categoryColors[event.category] || "#A19F9D", flexShrink: 0, marginTop: 4 }} />
                      {index < timelineEvents.length - 1 && <div style={{ width: 2, flex: 1, backgroundColor: "#EDEBE9", minHeight: 24 }} />}
                    </div>
                    <div style={{ paddingBottom: 20, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#323130" }}>{event.category.replace("Batch", "")}</div>
                      <div style={{ fontSize: 12, color: "#605E5C", marginTop: 2 }}>{formatDateTime(event.timestamp, useUtc)}</div>
                      <div style={{ fontSize: 13, color: "#323130", marginTop: 4 }}>{event.message}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          {activeTab === "documents" && (
            <div>
              {loadingDocs ? (
                <div style={{ textAlign: "center", padding: 40, color: "#605E5C" }}>Loading documents...</div>
              ) : (
                <>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th scope="col" style={{ padding: "0.375rem 0.5rem", textAlign: "left", fontSize: '0.75rem', fontWeight: 600, color: "#605E5C", borderBottom: "2px solid #EDEBE9" }}>Document ID</th>
                        <th scope="col" style={{ padding: "0.375rem 0.5rem", textAlign: "right", fontSize: '0.75rem', fontWeight: 600, color: "#605E5C", borderBottom: "2px solid #EDEBE9" }}>Sequence</th>
                        <th scope="col" style={{ padding: "0.375rem 0.5rem", textAlign: "left", fontSize: '0.75rem', fontWeight: 600, color: "#605E5C", borderBottom: "2px solid #EDEBE9" }}>Staged</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc) => (
                        <tr key={doc.id}>
                          <td style={{ padding: "0.5rem", fontSize: '0.8125rem', fontFamily: "monospace", borderBottom: "1px solid #F3F2F1", wordBreak: "break-all" }}>{doc.documentId}</td>
                          <td style={{ padding: "0.5rem", fontSize: '0.8125rem', textAlign: "right", borderBottom: "1px solid #F3F2F1" }}>{doc.sequenceId}</td>
                          <td style={{ padding: "0.5rem", fontSize: '0.75rem', borderBottom: "1px solid #F3F2F1" }}>{formatDateTime(doc.stagedDateTime, useUtc)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: '0.75rem', fontSize: '0.75rem', color: "#605E5C", flexWrap: "wrap", gap: '0.5rem' }}>
                    <span>{docTotalCount.toLocaleString()} documents total</span>
                    <div style={{ display: "flex", gap: '0.5rem', alignItems: "center", flexWrap: "wrap" }}>
                      <button onClick={() => loadDocuments(Math.max(0, docSkip - 25))} disabled={docSkip <= 0} aria-disabled={docSkip <= 0} aria-label="Previous page of documents" title="Go to the previous page of batch documents" style={{ padding: "0.25rem 0.75rem", fontSize: '0.75rem', border: "1px solid #EDEBE9", borderRadius: '0.25rem', backgroundColor: "#FFF", cursor: docSkip <= 0 ? "not-allowed" : "pointer", minHeight: '1.5rem' }}>Previous</button>
                      <span aria-live="polite">Page {Math.floor(docSkip / 25) + 1} / {Math.ceil(docTotalCount / 25)}</span>
                      <button onClick={() => loadDocuments(docSkip + 25)} disabled={docSkip + 25 >= docTotalCount} aria-disabled={docSkip + 25 >= docTotalCount} aria-label="Next page of documents" title="Go to the next page of batch documents" style={{ padding: "0.25rem 0.75rem", fontSize: '0.75rem', border: "1px solid #EDEBE9", borderRadius: '0.25rem', backgroundColor: "#FFF", cursor: docSkip + 25 >= docTotalCount ? "not-allowed" : "pointer", minHeight: '1.5rem' }}>Next</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {activeTab === "errors" && (
              <>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: "#D13438", margin: '0 0 0.75rem 0' }}>Retry History ({batch.errors.length} attempts)</h3>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th scope="col" style={{ padding: "0.375rem 0.5rem", textAlign: "center", fontSize: '0.75rem', fontWeight: 600, color: "#605E5C", borderBottom: "2px solid #EDEBE9" }}>#</th>
                      <th scope="col" style={{ padding: "0.375rem 0.5rem", textAlign: "left", fontSize: '0.75rem', fontWeight: 600, color: "#605E5C", borderBottom: "2px solid #EDEBE9" }}>DateTime</th>
                      <th scope="col" style={{ padding: "0.375rem 0.5rem", textAlign: "center", fontSize: '0.75rem', fontWeight: 600, color: "#605E5C", borderBottom: "2px solid #EDEBE9" }}>Code</th>
                      <th scope="col" style={{ padding: "0.375rem 0.5rem", textAlign: "left", fontSize: '0.75rem', fontWeight: 600, color: "#605E5C", borderBottom: "2px solid #EDEBE9" }}>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batch.errors.map((error) => (
                      <tr key={error.attempt}>
                        <td style={{ padding: "0.5rem", fontSize: '0.8125rem', textAlign: "center", borderBottom: "1px solid #F3F2F1" }}>{error.attempt}</td>
                        <td style={{ padding: "0.5rem", fontSize: '0.75rem', borderBottom: "1px solid #F3F2F1" }}>{formatDateTime(error.timestamp, useUtc)}</td>
                        <td style={{ padding: "0.5rem", fontSize: '0.8125rem', textAlign: "center", fontFamily: "monospace", borderBottom: "1px solid #F3F2F1", color: "#D13438" }}>{error.httpStatusCode || "—"}</td>
                        <td style={{ padding: "0.5rem", fontSize: '0.8125rem', borderBottom: "1px solid #F3F2F1", wordBreak: "break-word" }}>{error.errorMessage}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {batch.errors.length > 0 && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: "#FDE7E9", borderRadius: '0.25rem', fontSize: '0.8125rem', color: "#D13438", borderLeft: "0.25rem solid #D13438", wordBreak: "break-word" }}>
                    <strong><span aria-hidden="true">⚠ </span>Last Error:</strong> Supplier returned HTTP {batch.errors[batch.errors.length - 1].httpStatusCode} after {batch.errors.length} attempts.
                    <br /><strong>Recommendation:</strong> Check supplier service health, then retry via Admin.
                  </div>
                )}
              </>
            )}
        </div>
      </div>
    </>
  );
};
